import aa from 'search-insights';
import { v4 as uuidv4 } from 'uuid';
import type { AlgoliaObjectData } from '@repo/models/algolia/ObjectData';
import { USER_TOKEN_KEY } from '@repo/utils/algoliaConstants';

export type AlgoliaAnalyticsPayload = Record<
  string,
  string | number | boolean | string[] | number[] | AlgoliaObjectData[]
>;
const isBrowser = globalThis.window !== undefined;

const getUserTokenPayload = () => {
  const userToken = globalThis.localStorage.getItem(USER_TOKEN_KEY);
  const isAuthenticated = userToken?.startsWith('shopify-customer-');

  if (isAuthenticated) {
    return { userToken, authenticatedUserToken: userToken };
  }
  return { userToken };
};

const InitInsights = () => {
  aa('init', {
    appId: process.env.GATSBY_ALGOLIA_APP_ID,
    apiKey: process.env.GATSBY_ALGOLIA_SEARCH_KEY,
  });

  if (
    isBrowser &&
    globalThis.localStorage &&
    typeof globalThis.localStorage.getItem === 'function'
  ) {
    let uuid = globalThis.localStorage.getItem(USER_TOKEN_KEY);
    if (!uuid) {
      uuid = uuidv4();
      globalThis.localStorage.setItem(USER_TOKEN_KEY, uuid);
    }

    aa('setUserToken', uuid);

    aa(
      'onUserTokenChange',
      userToken => {
        globalThis.dataLayer?.push({
          algoliaUserToken: userToken,
        });
      },
      { immediate: true }
    );
  }
};

const TrackSearchView = (index: string, objectIDs: string[]) => {
  if (!isBrowser) return;

  aa('viewedObjectIDs', {
    ...getUserTokenPayload(),
    index,
    eventName: 'Search Results Viewed',
    objectIDs,
  });
};

const TrackClickAfterSearch = (
  insights: (name: string, params: AlgoliaAnalyticsPayload) => void,
  queryID?: string
) => {
  if (typeof insights !== 'function') return;

  const payload: AlgoliaAnalyticsPayload = {
    eventName: 'Search Result Clicked',
  };

  if (queryID) {
    payload.queryID = queryID;
  }

  insights('clickedObjectIDsAfterSearch', payload);
};

const TrackConversionAfterSearch = (queryID: string, objectIDs: string[]) => {
  if (!isBrowser) return;

  aa('convertedObjectIDsAfterSearch', {
    ...getUserTokenPayload(),
    index: 'products',
    eventName: 'Searched Product Added To Cart',
    queryID,
    objectIDs,
  });
};

const TrackClickObject = (
  objectIDs: string[],
  queryID: string | null,
  index: string = 'products',
  eventName: string = 'Product Clicked',
  positions?: number[]
) => {
  if (!isBrowser) return;

  const payload: AlgoliaAnalyticsPayload = {
    ...getUserTokenPayload(),
    index,
    eventName,
    objectIDs,
  };

  if (queryID) {
    payload.queryID = queryID;
    // positions are only valid when queryID is present (Algolia requirement)
    if (positions && positions.length > 0) {
      payload.positions = positions;
    }
  }

  aa('clickedObjectIDs', payload);
};

const TrackClickObjectAfterSearch = (
  objectIDs: string[],
  queryID: string | null | undefined,
  index: string = 'products',
  eventName: string = 'Product Clicked from Search Page',
  positions?: number[],
  metadata?: { source?: string; modelType?: string }
) => {
  if (!isBrowser || !queryID) return;

  const payload: AlgoliaAnalyticsPayload = {
    ...getUserTokenPayload(),
    index,
    eventName,
    queryID,
    objectIDs,
  };

  if (positions && positions.length > 0) {
    payload.positions = positions;
  }

  if (metadata?.source) {
    payload.source = metadata.source;
  }
  if (metadata?.modelType) {
    payload.modelType = metadata.modelType;
  }

  aa('clickedObjectIDsAfterSearch', payload);
};

const TrackAddedToCart = (
  objectIDs: string[],
  objectData: AlgoliaObjectData[],
  queryID: string | null,
  index: string = 'globalProducts',
  eventName: string = 'Product Added To Cart',
  currency?: string
) => {
  if (!isBrowser) return;

  const payload: AlgoliaAnalyticsPayload = {
    ...getUserTokenPayload(),
    index,
    eventName,
    objectIDs,
    objectData,
  };

  if (queryID) {
    payload.queryID = queryID;
  }

  if (currency) {
    payload.currency = currency;
  }

  aa('addedToCartObjectIDs', payload);
};

const TrackAddedToCartAfterSearch = (
  objectIDs: string[],
  objectData: AlgoliaObjectData[],
  queryID: string | null | undefined,
  index: string = 'globalProducts',
  eventName: string = 'Product Added To Cart After Search',
  currency?: string,
  metadata?: { source?: string; modelType?: string }
) => {
  if (!isBrowser || !queryID) return;

  const payload: AlgoliaAnalyticsPayload = {
    ...getUserTokenPayload(),
    index,
    eventName,
    queryID,
    objectIDs,
    objectData,
  };

  if (currency) {
    payload.currency = currency;
  }

  if (metadata?.source) {
    payload.source = metadata.source;
  }
  if (metadata?.modelType) {
    payload.modelType = metadata.modelType;
  }

  aa('addedToCartObjectIDsAfterSearch', payload);
};

const TrackClickFromPLP = (
  objectIDs: string[],
  queryID: string,
  index: string = 'products',
  positions?: number[]
) => {
  if (!isBrowser || !queryID) return;

  const payload: AlgoliaAnalyticsPayload = {
    ...getUserTokenPayload(),
    index,
    eventName: 'Product Clicked from PLP',
    queryID,
    objectIDs,
  };

  if (positions && positions.length > 0) {
    payload.positions = positions;
  }

  aa('clickedObjectIDsAfterSearch', payload);
};

const TrackAddedToCartFromPLP = (
  objectIDs: string[],
  objectData: AlgoliaObjectData[],
  queryID: string,
  index: string = 'globalProducts',
  currency?: string
) => {
  if (!isBrowser || !queryID) return;

  const payload: AlgoliaAnalyticsPayload = {
    ...getUserTokenPayload(),
    index,
    eventName: 'Product Added to Cart after PLP',
    queryID,
    objectIDs,
    objectData,
  };

  if (currency) {
    payload.currency = currency;
  }

  aa('addedToCartObjectIDsAfterSearch', payload);
};

const algoliaUserChanged = (customerShopifyId: string) => {
  if (!isBrowser || !globalThis?.localStorage) return;

  let uuid;
  if (customerShopifyId?.startsWith('gid://shopify/Customer/')) {
    const numericId = customerShopifyId.split('/').pop();
    uuid = `shopify-customer-${numericId}`;
  } else {
    uuid = uuidv4();
  }
  aa('setUserToken', uuid);
  globalThis?.localStorage.setItem(USER_TOKEN_KEY, uuid);
};

export {
  InitInsights,
  TrackSearchView,
  TrackConversionAfterSearch,
  TrackClickAfterSearch,
  TrackClickObject,
  TrackClickObjectAfterSearch,
  TrackAddedToCart,
  TrackAddedToCartAfterSearch,
  TrackClickFromPLP,
  TrackAddedToCartFromPLP,
  aa,
  algoliaUserChanged,
};
